 import { useState, useRef, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, X } from "lucide-react";
import baseUrl from "@/api/lib/url";
import { Button } from "../ui/button";
import { deleteFileApi } from "@/api/new-api/utility";
import { toast } from "react-hot-toast";
import { getActiveClientId, getActiveRestaurantId, getActiveRestaurantSystemId } from "@/lib/idProvider";

const FileUploader = ({
  className = "",
  onUploadComplete,
  label = "Upload Files",
  accept = "image/*",
  name = "files",
  onFilesChange,
  files = [],
  multiple = true,
  clientId,
  restaurantId,
  replace = false,
  directory,
  type,
}) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const resolvedClientId = clientId || getActiveClientId() || 1;
  const resolvedRestaurantId = restaurantId || getActiveRestaurantId() || 1;
  const resolvedSystemId = getActiveRestaurantSystemId() || resolvedRestaurantId;
  const resolvedDirectory = directory || `restaurant/${resolvedRestaurantId}/staff/image`;

  useEffect(() => {
    if (files?.length > 0) {
      setUploadedFiles(files);
    }
  }, [files]);

  const handleFileChange = async (e) => {
    const selectedFiles = Array.from(e.target.files);
    const token = localStorage.getItem("token");

    const newFiles = selectedFiles.map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      size: file.size,
      type: file.type,
      file,
      preview: file.type.startsWith("image/")
        ? URL.createObjectURL(file)
        : null,
      uploaded: false,
      error: null,
    }));

    setUploading(true);

    let updatedFiles = replace ? [] : [...uploadedFiles];

    for (const newFile of newFiles) {
      const formData = new FormData();
      formData.append("file", newFile.file);
      formData.append("client_id", resolvedClientId.toString());
      formData.append("restaurant_id", resolvedRestaurantId.toString());
      formData.append("restaurant_system_id", resolvedSystemId.toString());
      formData.append("directory", resolvedDirectory);

      try {
        const res = await fetch(`${baseUrl}utility/file-manager`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        });

        const data = await res.json();

        if (data.status === "success") {
          updatedFiles = replace ? [data.data] : [...updatedFiles, data.data];
          setUploadedFiles(updatedFiles);
          if (onUploadComplete) {
            onUploadComplete(updatedFiles); 
          }
          toast.success(data.message);
        } else {
          toast.error(data.message);
        }

        newFile.uploaded = true;
        newFile.file_manager_id = data?.data?.file_id || null;
        newFile.file_manager_url = data?.file_url || null;
      } catch (error) {
        newFile.error = error.message;
        toast.error(`Upload failed for ${newFile.name}: ${error.message}`);
      }
    }

    onFilesChange && onFilesChange(updatedFiles);
    setUploading(false);
    e.target.value = null;
  };

  const handleRemoveImage = (id) => {
    deleteFileApi(id)
      .then((response) => {
        // console.log("File deleted successfully:", response);
        if (response.status === "success") {
          toast.success("Image removed successfully.");
        } else {
          toast.error(response.message);
        }
        const updatedFiles = uploadedFiles.filter(
          (file) => (file.file_manager_id || file.file_id) !== id
        );

        // state update
        setUploadedFiles(updatedFiles);

        // parent component update
        onFilesChange && onFilesChange(updatedFiles);
      })
      .catch((error) => {
        console.error("Error deleting file:", error);
        toast.error("Failed to remove image. Please try again.");
      });
  };

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // console.log("uploadedFiles", uploadedFiles);

  return (
    <div className={`${className} flex flex-col gap-2`}>
      {type === "button" ? (
        <div>
          {uploadedFiles.length > 0 && uploadedFiles[0]?.file_manager_id && (
            <button
              type="button"
              onClick={() =>
                handleRemoveImage(uploadedFiles[0].file_manager_id)
              }
              className=" bg-cred mr-2 text-white p-1 rounded-full hover:bg-opacity-80"
            >
              <X size={14} />
            </button>
          )}
          <Button
            isLoading={uploading}
            type="button"
            onClick={handleButtonClick}
            className="w-32"
          >
            {label}
          </Button>
          <input
            ref={fileInputRef}
            id={name}
            name={name}
            type="file"
            accept={accept}
            multiple={multiple}
            onChange={handleFileChange}
            disabled={uploading}
            className="hidden"
          />
        </div>
      ) : (
        <div className="space-y-2 relative">
          <Label htmlFor={name} className="text-cwhite">
            {label}
          </Label>
          <div className="realative">
            <Input
              id={name}
              name={name}
              type="file"
              accept={accept}
              placeholder="Select files"
              multiple={multiple}
              onChange={handleFileChange}
              disabled={uploading}
              className="w-full placeholder:text-white text-transparent font-medium text-sm file:cursor-pointer cursor-pointer file:border-0 h-10 file:pt-1.5 file:px-4 file:mr-4 file:bg-transparent file:text-muted-foreground rounded"
            />
            {uploading && (
              <Loader2
                size={16}
                className="absolute animate-spin z-50 bottom-2.5  left-28"
              />
            )}
          </div>
        </div>
      )}

      {type !== "button" && uploadedFiles.length > 0 && (
        <div className="flex flex-wrap gap-3 p-2">
          {uploadedFiles.map((file, index) => (
            <div
              key={file.id || index}
              className="relative group w-24 h-24 rounded-md overflow-hidden border bg-muted/20"
            >
              <img
                src={file.preview || file.file_manager_url || file.file_url}
                alt={file.file_name || file.name}
                className="w-full h-full object-cover"
              />
              <button
                type="button"
                onClick={() =>
                  handleRemoveImage(file.file_manager_id || file.file_id)
                }
                className="absolute top-1 right-1 bg-cred text-white p-1 rounded-full hover:bg-opacity-80"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FileUploader;
